import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Building2,
  CheckCircle2,
  Factory,
  ShieldCheck,
  Store,
  Warehouse,
} from "lucide-react";
import { SEOHead } from "@/components/SEOHead";

const CommercialB2B = () => {
  const faqs = [
    {
      question: "What is your project minimum for commercial roofing?",
      answer:
        "We handle commercial projects of all sizes, but our typical minimum for a new flat roof installation is 2,000 square feet. For commercial repairs and maintenance, we have no strict minimum.",
    },
    {
      question: "Do you offer B2B billing and net terms?",
      answer:
        "Yes, for established commercial clients and property management firms, we offer flexible B2B billing options including Net 30 terms upon credit approval.",
    },
    {
      question: "Are your crews certified for commercial installations?",
      answer:
        "Absolutely. Our crews are fully certified by the manufacturers for the TPO, EPDM, and modified bitumen systems we install, ensuring your warranty is fully valid.",
    },
  ];

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        serviceType: "Commercial Roofing",
        provider: {
          "@id": "https://www.saskroofingcompany.com/#localbusiness",
        },
        areaServed: [
          { "@type": "City", name: "Saskatoon" },
          { "@type": "City", name: "Regina" },
          { "@type": "City", name: "Moose Jaw" },
          { "@type": "City", name: "Prince Albert" },
          { "@type": "City", name: "Swift Current" },
          { "@type": "City", name: "Yorkton" },
          { "@type": "City", name: "Estevan" },
          { "@type": "City", name: "Weyburn" },
        ],
      },
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.answer,
          },
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://www.saskroofingcompany.com/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Commercial Roofing",
            item: "https://www.saskroofingcompany.com/commercial",
          },
        ],
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="Commercial Roofing Saskatchewan | TPO, EPDM & Metal"
        description="Commercial roofing contractor in Saskatchewan. We install TPO, EPDM, and metal roofing for warehouses, retail, and industrial buildings with B2B billing."
        canonical="https://www.saskroofingcompany.com/commercial"
        schemaJson={[schema]}
      />

      {/* Hero Section */}
      <section className="relative pt-20 pb-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/c806e88c-865d-4eaa-b609-dfdceee70f67.png&output=webp&q=60"
            alt="Commercial flat roof installation in Saskatchewan"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/80 to-primary/60" />
        </div>
        <div className="container px-4 relative z-10 text-primary-foreground">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Commercial Roofing Saskatchewan
            </h1>
            <p className="text-lg md:text-xl mb-8 text-primary-foreground/90 leading-relaxed">
              When it comes to commercial property, your roof protects your
              inventory, your tenants, and your revenue. We understand the
              unique needs of commercial buyers: strict per-square-foot pricing,
              B2B billing setups, comprehensive warranty documentation, and
              minimizing business interruption during installation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                className="bg-accent text-accent-foreground hover:bg-accent/90 text-lg px-8"
                asChild
              >
                <Link to="/contact">Request a Commercial Bid</Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground/10 text-lg px-8"
                asChild
              >
                <a href="tel:6397397969">Call (639) 739-7969</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stat Callout Grid */}
      <section className="py-12 bg-muted border-b border-border">
        <div className="container px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <Card className="bg-card text-card-foreground text-center border-none shadow-sm">
              <CardContent className="pt-6">
                <div className="text-3xl font-bold text-accent mb-2">500+</div>
                <p className="text-sm font-medium">
                  Commercial Projects Completed
                </p>
              </CardContent>
            </Card>
            <Card className="bg-card text-card-foreground text-center border-none shadow-sm">
              <CardContent className="pt-6">
                <div className="text-3xl font-bold text-accent mb-2">
                  Net 30
                </div>
                <p className="text-sm font-medium">
                  B2B Billing Setup Available
                </p>
              </CardContent>
            </Card>
            <Card className="bg-card text-card-foreground text-center border-none shadow-sm">
              <CardContent className="pt-6">
                <div className="text-3xl font-bold text-accent mb-2">20-Yr</div>
                <p className="text-sm font-medium">Warranty Documentation</p>
              </CardContent>
            </Card>
            <Card className="bg-card text-card-foreground text-center border-none shadow-sm">
              <CardContent className="pt-6">
                <div className="text-3xl font-bold text-accent mb-2">
                  2k sq ft
                </div>
                <p className="text-sm font-medium">Project Minimum Size</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Systems We Install */}
      <section className="py-20 bg-background">
        <div className="container px-4">
          <div className="max-w-3xl mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Commercial Systems We Install
            </h2>
            <p className="text-lg text-muted-foreground">
              We specialize in the primary flat and low-slope roofing systems
              used across Saskatchewan's commercial sector. Each system offers
              distinct advantages depending on your building's footprint, foot
              traffic, and budget.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <div className="space-y-4">
              <h3 className="text-2xl font-bold">
                TPO (Thermoplastic Polyolefin)
              </h3>
              <p className="text-muted-foreground">
                The fastest-growing commercial roofing system. TPO reflects UV
                rays, reducing cooling costs in the summer, and features
                heat-welded seams for superior waterproofing. It's highly
                resistant to chemical exposure and punctures.
              </p>
            </div>
            <div className="space-y-4">
              <h3 className="text-2xl font-bold">EPDM (Rubber Roofing)</h3>
              <p className="text-muted-foreground">
                A time-tested, durable synthetic rubber membrane. EPDM is
                exceptionally flexible, making it ideal for Saskatchewan's
                extreme temperature swings. It has a proven track record of
                lasting 25-30 years with proper maintenance.
              </p>
            </div>
            <div className="space-y-4">
              <h3 className="text-2xl font-bold">Modified Bitumen</h3>
              <p className="text-muted-foreground">
                An evolution of traditional built-up roofing (BUR). This
                multi-ply system is incredibly tough and handles heavy foot
                traffic well (e.g., frequent HVAC maintenance). It performs
                exceptionally well in cold weather.
              </p>
            </div>
            <div className="space-y-4">
              <h3 className="text-2xl font-bold">Standing Seam Metal</h3>
              <p className="text-muted-foreground">
                For sloped commercial buildings, standing seam metal offers a
                50+ year lifespan with minimal maintenance. It sheds snow easily
                and stands up to the harshest prairie hailstorms.
              </p>
            </div>
          </div>

          {/* Comparison Table */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[200px]">System</TableHead>
                  <TableHead>Cost / Sq Ft</TableHead>
                  <TableHead>Expected Lifespan</TableHead>
                  <TableHead>Climate Fit (SK)</TableHead>
                  <TableHead>Maintenance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="font-medium">TPO</TableCell>
                  <TableCell>$8 - $12</TableCell>
                  <TableCell>20 - 25 Years</TableCell>
                  <TableCell>Excellent (UV Reflective)</TableCell>
                  <TableCell>Low</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">EPDM</TableCell>
                  <TableCell>$7 - $11</TableCell>
                  <TableCell>25 - 30 Years</TableCell>
                  <TableCell>Excellent (Flexible in cold)</TableCell>
                  <TableCell>Low to Medium</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">
                    Modified Bitumen
                  </TableCell>
                  <TableCell>$9 - $14</TableCell>
                  <TableCell>20 - 25 Years</TableCell>
                  <TableCell>Very Good (Durable)</TableCell>
                  <TableCell>Medium</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">
                    Standing Seam Metal
                  </TableCell>
                  <TableCell>$15 - $22</TableCell>
                  <TableCell>50+ Years</TableCell>
                  <TableCell>Outstanding (Hail/Snow)</TableCell>
                  <TableCell>Very Low</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
          <p className="text-sm text-muted-foreground mt-4 italic">
            * Commercial flat roof replacements in Saskatchewan typically run
            $8-$16/sq ft installed. Pricing varies based on insulation needs,
            tear-off requirements, and roof complexity.
          </p>
        </div>
      </section>

      {/* Industries Served */}
      <section className="py-20 bg-muted">
        <div className="container px-4">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Industries We Serve
            </h2>
            <p className="text-lg text-muted-foreground">
              We understand the operational requirements of different commercial
              sectors.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            <Card className="border-none shadow-sm text-center">
              <CardContent className="pt-6">
                <Warehouse className="h-12 w-12 mx-auto text-accent mb-4" />
                <h3 className="font-bold text-lg">Warehousing & Logistics</h3>
              </CardContent>
            </Card>
            <Card className="border-none shadow-sm text-center">
              <CardContent className="pt-6">
                <Building2 className="h-12 w-12 mx-auto text-accent mb-4" />
                <h3 className="font-bold text-lg">Multi-Family & Strata</h3>
              </CardContent>
            </Card>
            <Card className="border-none shadow-sm text-center">
              <CardContent className="pt-6">
                <Store className="h-12 w-12 mx-auto text-accent mb-4" />
                <h3 className="font-bold text-lg">Retail Centers</h3>
              </CardContent>
            </Card>
            <Card className="border-none shadow-sm text-center">
              <CardContent className="pt-6">
                <Factory className="h-12 w-12 mx-auto text-accent mb-4" />
                <h3 className="font-bold text-lg">Light Industrial</h3>
              </CardContent>
            </Card>
            <Card className="border-none shadow-sm text-center">
              <CardContent className="pt-6">
                <Building2 className="h-12 w-12 mx-auto text-accent mb-4" />
                <h3 className="font-bold text-lg">Office Buildings</h3>
              </CardContent>
            </Card>
            <Card className="border-none shadow-sm text-center">
              <CardContent className="pt-6">
                <Warehouse className="h-12 w-12 mx-auto text-accent mb-4" />
                <h3 className="font-bold text-lg">Agricultural Facilities</h3>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Process & Warranty */}
      <section className="py-20 bg-background">
        <div className="container px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl font-bold mb-8">
                Our Commercial Process
              </h2>
              <div className="space-y-8">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-8 h-8 rounded-full bg-accent text-accent-foreground flex items-center justify-center font-bold">
                      1
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">
                      RFQ & Initial Consultation
                    </h3>
                    <p className="text-muted-foreground">
                      We review your requirements, blueprints, or existing roof
                      issues to understand the scope.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-8 h-8 rounded-full bg-accent text-accent-foreground flex items-center justify-center font-bold">
                      2
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">
                      Comprehensive Site Visit
                    </h3>
                    <p className="text-muted-foreground">
                      Core sampling, drainage analysis, and exact measurements
                      to ensure an accurate bid.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-8 h-8 rounded-full bg-accent text-accent-foreground flex items-center justify-center font-bold">
                      3
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">
                      Detailed Formal Bid
                    </h3>
                    <p className="text-muted-foreground">
                      A line-item proposal including materials, labor, tear-off,
                      and projected timelines.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-8 h-8 rounded-full bg-accent text-accent-foreground flex items-center justify-center font-bold">
                      4
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">
                      Contract & Installation
                    </h3>
                    <p className="text-muted-foreground">
                      Clear payment schedules, safety planning, and execution
                      with minimal business interruption.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-8 h-8 rounded-full bg-accent text-accent-foreground flex items-center justify-center font-bold">
                      5
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">
                      Documentation & Handover
                    </h3>
                    <p className="text-muted-foreground">
                      Final walkthrough, warranty registration, and maintenance
                      guidelines provided to your management team.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <Card className="bg-primary text-primary-foreground border-none">
                <CardContent className="p-8">
                  <ShieldCheck className="h-12 w-12 text-accent mb-6" />
                  <h3 className="text-2xl font-bold mb-4">
                    Warranty & Insurance Documentation
                  </h3>
                  <p className="text-primary-foreground/90 mb-6 leading-relaxed">
                    Commercial projects demand rigorous compliance. We provide
                    complete documentation for your records and your
                    stakeholders.
                  </p>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="h-6 w-6 text-accent shrink-0" />
                      <span>
                        <strong>WCB Clearance:</strong> Full workers'
                        compensation coverage for all crews on site.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="h-6 w-6 text-accent shrink-0" />
                      <span>
                        <strong>Commercial Liability:</strong> $5M+ general
                        liability insurance specific to commercial roofing
                        operations.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="h-6 w-6 text-accent shrink-0" />
                      <span>
                        <strong>NDL Warranties:</strong> No Dollar Limit (NDL)
                        manufacturer warranties available on certified TPO and
                        EPDM installations.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="h-6 w-6 text-accent shrink-0" />
                      <span>
                        <strong>Workmanship Guarantee:</strong> Written
                        guarantees on all installation labor.
                      </span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-muted">
        <div className="container px-4 max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Commercial Roofing FAQ</h2>
            <p className="text-lg text-muted-foreground">
              Common questions from property managers and building owners.
            </p>
          </div>

          <Accordion
            type="single"
            collapsible
            className="w-full bg-card rounded-lg p-6 shadow-sm"
          >
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left font-bold text-lg">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 bg-primary text-primary-foreground text-center">
        <div className="container px-4 max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Discuss Your Commercial Project?
          </h2>
          <p className="text-xl mb-10 text-primary-foreground/90">
            Contact us to request a formal commercial bid or schedule a
            comprehensive site visit for your facility.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button
              size="lg"
              className="bg-accent text-accent-foreground hover:bg-accent/90 text-lg px-8"
              asChild
            >
              <Link to="/contact">Request a Commercial Bid</Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground/10 text-lg px-8"
              asChild
            >
              <a href="tel:6397397969">Call (639) 739-7969</a>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
};

export default CommercialB2B;
